import { call, put, takeLatest, select, cancelled } from "redux-saga/effects";
import i18n from "../../i18n";
import { fetchCompanyUsersRequest, fetchCompanyUsersSuccess, fetchCompanyUsersFailure } from "../slices/database/companyUsersSlice";
import { fetchUserCompaniesRequest, fetchUserCompaniesSuccess, fetchUserCompaniesFailure } from "../slices/database/userCompaniesSlice";
import { fetchUserAccessDataRequest, fetchUserAccessDataSuccess, fetchUserAccessDataFailure } from "../slices/database/userAccessDataSlice";
import { fetchAccessDataRequest, fetchAccessDataSuccess, fetchAccessDataFailure } from "../slices/database/accessDataSlice";
import { fetchWorkflowAccessRequest, fetchWorkflowAccessSuccess, fetchWorkflowAccessFailure } from "../slices/database/workflowAccessSlice";
import { setLanguage, loadTranslationsRequest, loadTranslationsSuccess, loadTranslationsFailure } from "../slices/database/translationSlice";
import { fetchTableColumnsRequest, fetchTableColumnsSuccess, fetchTableColumnsFailure } from "../slices/database/tableColumnConfigSlice";
import { getAuthzCompanies, getUserAccessInfo, getAccessData, getCompanyWorkflows, getCompanyUsers, getTemplatesData, getAllDatabaseRecord, getLabelsData, getTableColumnConfigs } from "../../api/databaseApi";
import { transformApiResponse } from "../../hooks/useTableColumns";
import { callWithAbort, formatSagaError } from "../../helpers/sagaUtils";
import { transformTranslationData } from "../../helpers/translationUtils";
import { logger } from "../../helpers/logger";
import { fetchTemplatesFailure, fetchTemplatesRequest, fetchTemplatesSuccess } from "store/slices/database/templatesSlice";
import { fetchLabelsDataThunk } from "store/thunks/mdsThunks";

function* getUserCompaniesSaga() {
	// authz.* schema: identity comes from the token, no userId argument needed anymore.
	const response = yield* callWithAbort(
		getAuthzCompanies,
		[],
		fetchUserCompaniesSuccess,
		fetchUserCompaniesFailure,
		"getUserCompaniesSaga"
	);

	if (response) {
		logger.info("✅ getUserCompaniesSaga - Response:", response);
	}
}

function* getCompanyUsersSaga(action) {
	const receivedPayload = action.payload;

	logger.info("🔍 getCompanyUsersSaga - Received payload:", receivedPayload);
	logger.info("🔍 Payload type:", typeof receivedPayload);

	const response = yield* callWithAbort(
		getCompanyUsers,
		[receivedPayload.companyId],
		fetchCompanyUsersSuccess,
		fetchCompanyUsersFailure,
		"getCompanyUsersSaga"
	);

	if (response) {
		logger.info("✅ getCompanyUsersSaga - Response:", response);
	}
}

function* getUserAccessDataSaga(action) {
	const receivedPayload = action.payload;

	logger.info("🔍 getUserAccessDataSaga - Received payload:", receivedPayload);

	const response = yield* callWithAbort(
		getUserAccessInfo,
		[receivedPayload.userId, receivedPayload.companyId],
		fetchUserAccessDataSuccess,
		fetchUserAccessDataFailure,
		"getUserAccessDataSaga"
	);

	if (response) {
		logger.info("✅ getUserAccessDataSaga - Response:", response);
	}
}

function* getAccessDataSaga(action) {
	const receivedPayload = action.payload;

	logger.info("🔍 getAccessDataSaga - Received payload:", receivedPayload);

	const response = yield* callWithAbort(
		getAccessData,
		[receivedPayload.companyId],
		fetchAccessDataSuccess,
		fetchAccessDataFailure,
		"getAccessDataSaga"
	);

	if (response) {
		logger.info("✅ getAccessDataSaga - Response:", response);
	}
}

function* getWorkflowAccessSaga(action) {
	yield* callWithAbort(
		getCompanyWorkflows,
		[action.payload.companyId],
		fetchWorkflowAccessSuccess,
		fetchWorkflowAccessFailure,
		"getWorkflowAccessSaga"
	);
}

function* loadTranslationsSaga(action) {
	const language = action.payload;
	const abortController = new AbortController();
	try {
		yield put(loadTranslationsRequest());

		// Check if already loaded
		const resources = yield select((state) => state.translations.resources);
		if (resources[language]) {
			logger.info("🔍 loadTranslationsSaga - Using cached translations for:", language);
			// Already loaded, just change language in i18n
			yield call([i18n, i18n.changeLanguage], language);
			yield put(
				loadTranslationsSuccess({
					language,
					translations: resources[language].translation,
				})
			);
			return;
		}

		logger.info("🔍 loadTranslationsSaga - Fetching translations for:", language);

		// Fetch from API with abort support
		const response = yield call(getLabelsData, language, abortController.signal);

		// Transform response to translations object
		const translations = transformTranslationData(response);

		// Update Redux
		yield put(loadTranslationsSuccess({ language, translations }));

		// Add to i18n and change language
		yield call([i18n, i18n.addResourceBundle], language, "translation", translations, true, true);
		yield call([i18n, i18n.changeLanguage], language);

		logger.info("✅ loadTranslationsSaga - Translations loaded successfully for:", language);
	} catch (error) {
		if (error.name === "AbortError") {
			logger.info(`🚫 loadTranslationsSaga - Request cancelled`);
			return;
		}

		logger.error("❌ loadTranslationsSaga - Error:", error);
		yield put(loadTranslationsFailure(formatSagaError(error)));
	} finally {
		if (yield cancelled()) {
			logger.info(`🛑 loadTranslationsSaga - Saga cancelled, aborting request`);
			abortController.abort();
		}
	}
}

function* fetchTemplatesSaga(action) {
	const abortController = new AbortController();

	try {
		logger.info("🚀 Fetching templates");

		const response = yield call(getTemplatesData, abortController.signal);
		logger.info("✅ Successfully fetched templates");
		yield put(fetchTemplatesSuccess(response));
	} catch (error) {
		if (error.name === "AbortError") {
			logger.info("🚫 Request cancelled");
			return;
		}
		logger.error("❌ Failed to fetch data", error);
		yield put(fetchTemplatesFailure(error.message));
	} finally {
		if (yield cancelled()) {
			logger.info("🛑 Saga cancelled, aborting request");
			abortController.abort();
		}
	}
}

function* fetchTableColumnConfigsSaga(action) {
	const { tableName, domainId } = action.payload;
	const abortController = new AbortController();

	try {
		logger.info("🚀 Fetching table column configs for:", tableName);

		const response = yield call(getTableColumnConfigs, domainId, tableName, abortController.signal);

		// A response that says it failed is an error (-> template fallback), not "no columns".
		if (!response?.success) {
			throw new Error(response?.message || "Failed to fetch column configs");
		}

		const transformedColumns = transformApiResponse(response.data?.records || []);
		if (transformedColumns.length === 0) {
			logger.warn("⚠️ No column configs found for:", tableName);
		} else {
			logger.info("✅ Successfully fetched column configs:", transformedColumns);
		}
		yield put(fetchTableColumnsSuccess({ tableName, columns: transformedColumns }));
	} catch (error) {
		if (error.name === "AbortError") {
			logger.info("🚫 fetchTableColumnConfigsSaga - Request cancelled");
			return;
		}
		logger.error("❌ Failed to fetch column configs:", error);
		yield put(fetchTableColumnsFailure({ tableName, error: error.message || "Failed to fetch column configs" }));
	} finally {
		if (yield cancelled()) {
			logger.info("🛑 fetchTableColumnConfigsSaga - Saga cancelled, aborting request");
			abortController.abort();
		}
	}
}

export default function* databaseSaga() {
	yield takeLatest(fetchCompanyUsersRequest.type, getCompanyUsersSaga);
	yield takeLatest(fetchUserCompaniesRequest.type, getUserCompaniesSaga);
	yield takeLatest(fetchUserAccessDataRequest.type, getUserAccessDataSaga);
	yield takeLatest(fetchAccessDataRequest.type, getAccessDataSaga);
	yield takeLatest(fetchWorkflowAccessRequest.type, getWorkflowAccessSaga);
	yield takeLatest(setLanguage.type, loadTranslationsSaga);
	yield takeLatest(fetchTemplatesRequest.type, fetchTemplatesSaga);
	yield takeLatest(fetchTableColumnsRequest.type, fetchTableColumnConfigsSaga);
}
